import { Box, Typography, useTheme } from '@mui/material';
import { forwardRef } from 'react';
import { HeroV2Body, HeroV2Heading, HeroV2ButtonsCollection, ContentTypeRichTextContent, TermsAndConditions as TTermsAndConditions, SmallScreenContent, HeroV2PromoHeadline } from '@/types/generated';
import { resolveEyebrowTextColor, resolveTACColor } from './HeroContent.helpers';
import { TACWrapperStyles, eyebrowStyles, getBodyContainerStyles, getInnerWrapperStyles, getWrapperStyles, getHeroButtonWrapperStyles, heroHeadingStyles, getPromoHeadlineSizeVariant, getHeroHeadingVariant, outerWrapperStyles } from './HeroContent.styles';
import ContentfulButton from '@shared-components/components/ContentfulButton';
import ContentTypeRichText from '@shared-components/components/ContentTypeRichText';
import TermsAndConditions from '@shared-components/components/TermsAndConditions';
import { Maybe, THeroContentWidth } from '@shared-components/types';
interface IHeroContent {
  backgroundColorSmallScreen?: Maybe<string>;
  body?: Maybe<HeroV2Body>;
  bodySize?: Maybe<string>;
  buttons?: HeroV2ButtonsCollection['items'];
  contentAlign?: Maybe<string>;
  contentPosition?: Maybe<string>;
  contentWidth?: Maybe<THeroContentWidth>;
  darkTextColor?: Maybe<boolean>;
  eyebrow?: Maybe<string>;
  eyebrowStyle?: Maybe<string>;
  hasVideoBackground: boolean;
  heading?: Maybe<HeroV2Heading>;
  headingSize?: Maybe<string>;
  isSmallScreen: boolean;
  mediaAspectRatioSmallScreen?: Maybe<string>;
  promoHeadline?: Maybe<HeroV2PromoHeadline>;
  promoHeadlineSize?: Maybe<string>;
  termsConditions?: Maybe<TTermsAndConditions>;
  contentSmallScreen?: Maybe<SmallScreenContent>;
  children?: React.ReactNode;
  isNoContent?: boolean;
}
type HeroEyebrowSectionProps = {
  eyebrow?: Maybe<string>;
  eyebrowText?: Maybe<string>;
  eyebrowStyle?: Maybe<string>;
  isSmallScreen: boolean;
  isDarkBackground: boolean;
  darkTextColor?: Maybe<boolean>;
  mediaAspectRatioSmallScreen?: Maybe<string>;
};
type HeroRichTextSectionProps = {
  shouldRender?: Maybe<ContentTypeRichTextContent>;
  content?: Maybe<ContentTypeRichTextContent>;
  dataTestId: string;
  contentAlign?: Maybe<string>;
  isSmallScreen: boolean;
  darkTextColor?: Maybe<boolean>;
  variant: string;
  containerSx?: Record<string, unknown>;
};
const getBodyVariant = (isSmallScreen: boolean, bodySize?: Maybe<string>) => {
  if (isSmallScreen) {
    return 'bodyMediumBook';
  }
  if (bodySize === 'Medium') {
    return 'bodyLargeBook';
  }
  return 'bodyXLBook';
};
const getLeftAlignedSx = (contentAlign?: Maybe<string>) => {
  return contentAlign === 'Left' ? {
    marginLeft: 0
  } : {};
};
function HeroEyebrowSection({
  eyebrow,
  eyebrowText,
  eyebrowStyle,
  isSmallScreen,
  isDarkBackground,
  darkTextColor,
  mediaAspectRatioSmallScreen
}: Readonly<HeroEyebrowSectionProps>) {
  if (!eyebrow) {
    return null;
  }
  const isPromoEyebrow = eyebrowStyle === 'Promo';
  return <Typography sx={eyebrowStyles} color={resolveEyebrowTextColor({
    isSmallScreen,
    isDarkBackground,
    darkTextColor,
    isPromoEyebrow,
    mediaAspectRatioSmallScreen
  })} data-testid="text_hero_eyebrow" fontWeight={isPromoEyebrow ? 700 : 400} variant={isPromoEyebrow ? 'bodyXLBook' : 'bodyLargeBook'} data-sentry-element="Typography" data-sentry-component="HeroEyebrowSection" data-sentry-source-file="HeroContent.tsx">
			{eyebrowText}
		</Typography>;
}
function HeroRichTextSection({
  shouldRender,
  content,
  dataTestId,
  contentAlign,
  isSmallScreen,
  darkTextColor,
  variant,
  containerSx = {}
}: Readonly<HeroRichTextSectionProps>) {
  if (!shouldRender) {
    return null;
  }
  return <ContentTypeRichText disableGutters disablePaddingOn={['Top', 'Bottom']} contentMaxWidth="100%" backgroundColor="transparent" dataTestId={dataTestId} centerAlign={isSmallScreen || contentAlign === 'Center'} content={content} containerSx={{
    ...containerSx,
    ...getLeftAlignedSx(contentAlign)
  }} docProps={{
    color: darkTextColor ? 'text.primary' : 'text.light',
    variant
  }} data-sentry-element="ContentTypeRichText" data-sentry-component="HeroRichTextSection" data-sentry-source-file="HeroContent.tsx" />;
}
const HeroContent = forwardRef<HTMLDivElement, IHeroContent>(({
  backgroundColorSmallScreen,
  body,
  bodySize,
  buttons,
  contentAlign,
  contentPosition,
  contentWidth = 'Medium',
  darkTextColor,
  eyebrow,
  eyebrowStyle,
  hasVideoBackground,
  heading,
  headingSize,
  isSmallScreen,
  mediaAspectRatioSmallScreen,
  promoHeadline,
  promoHeadlineSize,
  termsConditions,
  contentSmallScreen,
  children,
  isNoContent
}, ref) => {
  const isDarkBackground = !!backgroundColorSmallScreen?.toLowerCase().includes('dark');
  const {
    bodySmallScreen,
    eyebrowSmallScreen,
    headingSmallScreen
  } = new Object(contentSmallScreen) as SmallScreenContent;
  const eyebrowText = isSmallScreen && eyebrowSmallScreen ? eyebrowSmallScreen : eyebrow;
  const headingContent = isSmallScreen && headingSmallScreen ? headingSmallScreen : heading;
  const bodyContent = isSmallScreen && bodySmallScreen ? bodySmallScreen : body;
  const theme = useTheme();
  const bodyVariant = getBodyVariant(isSmallScreen, bodySize);
  if (isSmallScreen && isNoContent) {
    return null;
  }
  return <Box data-testid="section_hero_content" sx={getWrapperStyles({
    isSmallScreen,
    contentPosition,
    backgroundColorSmallScreen,
    theme,
    mediaAspectRatioSmallScreen
  })}>
				{children}
				<Box sx={isSmallScreen ? null : outerWrapperStyles(contentWidth)} ref={ref} data-testid="hero_content_main_box">
					<Box sx={getInnerWrapperStyles(isSmallScreen, contentAlign)} data-testid="hero_content_inner_box">
						<HeroEyebrowSection eyebrow={eyebrow} eyebrowText={eyebrowText} eyebrowStyle={eyebrowStyle} isSmallScreen={isSmallScreen} isDarkBackground={isDarkBackground} darkTextColor={darkTextColor} mediaAspectRatioSmallScreen={mediaAspectRatioSmallScreen} />

						<HeroRichTextSection shouldRender={promoHeadline as ContentTypeRichTextContent} content={promoHeadline as ContentTypeRichTextContent} dataTestId="text_promo_headline" contentAlign={contentAlign} isSmallScreen={isSmallScreen} darkTextColor={darkTextColor} variant={getPromoHeadlineSizeVariant(promoHeadlineSize as string, isSmallScreen)} />

						<HeroRichTextSection shouldRender={heading as ContentTypeRichTextContent} content={headingContent as ContentTypeRichTextContent} dataTestId="group_hero_heading" contentAlign={contentAlign} isSmallScreen={isSmallScreen} darkTextColor={darkTextColor} variant={getHeroHeadingVariant(headingSize as string, isSmallScreen)} containerSx={heroHeadingStyles} />

						{body && <ContentTypeRichText disableGutters disablePaddingOn={['Top', 'Bottom']} contentMaxWidth="100%" dataTestId="group_hero_body" backgroundColor="transparent" centerAlign={isSmallScreen || contentAlign === 'Center'} content={bodyContent as ContentTypeRichTextContent} containerSx={{
          ...getBodyContainerStyles(contentAlign || 'center', !!darkTextColor, isSmallScreen),
          ...getLeftAlignedSx(contentAlign)
        }} docProps={{
          color: darkTextColor ? 'text.secondary' : 'text.light',
          variant: bodyVariant
        }} />}

						{buttons && buttons.length > 0 && <Box data-testid="group_hero_buttons_wrapper" sx={getHeroButtonWrapperStyles(contentAlign, isSmallScreen)}>
								{buttons.map(button => button ? <ContentfulButton key={button.sys.id} {...button} dataTestId="button_hero_cta" /> : null)}
							</Box>}
					</Box>
					<Box>
						{termsConditions && <Box data-testid="group_hero_terms_and_conditions_wrapper" sx={TACWrapperStyles}>
								<TermsAndConditions {...termsConditions} textColor={resolveTACColor(hasVideoBackground, !!darkTextColor)} />
							</Box>}
					</Box>
				</Box>
			</Box>;
});
HeroContent.displayName = 'HeroContent';
export default HeroContent;